
import {
  ref,
  computed,
  defineComponent,
  onMounted,
  onUpdated,
  onBeforeUnmount,
  watch,
  reactive,
  toRefs,
  Ref
} from 'vue'

import { debounce, DebouncedFunc } from 'lodash'
import { uuid } from '@/utils'
import SpatialBoundary, { BoundingBox, RNum, SpatialEntity } from '@/pages/Manage/Inventory/Warehouse/components/MapGrid'
import { difference } from '@/utils/set'

interface Point {
  x: number,
  y: number
}

interface Box {
  width: number,
  height: number
}

interface SelectionBoundingBox {
  top: number,
  left: number,
  width: number,
  height: number
  bottom?: number,
  right?: number
}

interface SelectorState {
  mouseDown: boolean,
  concat: boolean,
  startPoint: Point & { offsetX: number, offsetY: number } | null,
  endPoint: Point | null,
  selection: Set<SpatialEntity>,
  selector: string | null,
  path: Array<string> | string | null,
  staticOffset: Point
}

interface SelectionBoxStyle {
  background: string,
  top?: string,
  left?: string,
  width?: string,
  height?: string
}

export default defineComponent({
  name: 'SelectableArea',
  props: {
    // NOTE: if the items to be selected are nested within the selectable area (i.e in a container element),
    //       make sure to pass a path-array containing an absolute path to the element(s)
    selector: {
      type: [String, Array],
      required: true
    },
    color: {
      type: String,
      default: 'rgb(75, 121, 240, .3)'
    },
    debug: {
      type: [Boolean, Number] ,
      default: false
    },
    id: {
      type: String,
      default: Date.now().toString(16)
    },
    ticks: {
      type: Number,
      default: 1
    }
  },
  emits: ['selection'],
  setup (props, { emit }) {
    const el = ref()
    const selectorId = ref(`selector-${props.id}-${(Math.random()*100).toFixed(0)}`)
    const state = reactive<SelectorState>({
      mouseDown: false,
      concat: false,
      startPoint: null,
      endPoint: null,
      selection: new Set(),
      selector: null,
      path: null,
      staticOffset: { x: 0, y: 0 }
    })

    // create spatial map etc.
    const space: Ref<SpatialBoundary> = ref() as any

    const selectionBox = computed<[number, number, number, number]>(() => {
      if (!state.mouseDown || !state.startPoint || !state.endPoint) return [0,0,0,0]

      const s = getScroll(),
            sp = state.startPoint,
            ep = state.endPoint;
      return ([
        Math.max(0, Math.min(sp.x, ep.x) - s.x - sp.offsetX),
        Math.max(0, Math.min(sp.y, ep.y) - s.y - sp.offsetY),
        Math.abs(Math.max(0, sp.x) - Math.max(0, ep.x)),
        Math.abs(Math.max(0, sp.y) - Math.max(0, ep.y))
      ])
    })

    const selectionBoxStyling = computed<SelectionBoxStyle>(() => {
      // Only set styling when necessary
      if (!state.mouseDown || !state.startPoint || !state.endPoint) {
        return { background: props.color }
      }
      const [ left, top, width, height ] = selectionBox.value

      // Return the styles to be applied
      return {
        background: props.color,
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        height: `${height}px`
      }
    })

    // gets the cumulative offset for the given path
    function getPathOffset2D (path = state.path): Point {
      if (!path || typeof path === 'string') return { x: 0, y: 0 }

      // traverse the path and sum the offsets (firt element in case of a class)
      const offset = path.reduce((curr, selector, i) => {
        const el:HTMLElement | null = document.querySelector(selector)
        if (!el) return curr
        return { x: curr.x + el.offsetTop, y: curr.y + el.offsetLeft }
      }, { x: 0, y: 0 })

      return offset
    }

    function computeElementRelativeOffset (element:HTMLElement):Point {
      return { x: 0, y: 0 }
    }

    function initSpace(): void {
      const boundaries = el.value.getBoundingClientRect()

      space.value = new SpatialBoundary(boundaries.width, boundaries.height, 0, { ocant_threshold: 20 })

      const children:Array<HTMLElement> = el.value.$children?.length ? el.value.$children : el.value.children
      if (!children) return

      const s: Array<string> = Array.isArray(props.selector) ? [...props.selector] as Array<string> : [props.selector]
      state.selector = s.pop() as string
      state.path = s

      const selectorString = state.path.length ? `${state.path.join(' ')} ${state.selector}` : state.selector
      const items: Array<HTMLElement> = state.selector && !state.path?.length ? children : Array.from(document.querySelectorAll(selectorString))

      items.forEach(item => {
        const offset = computeElementRelativeOffset(item)
        const id = uuid()
        item.setAttribute('data-selection-id', id)
        item.setAttribute('data-offset-x', String(offset.x))
        item.setAttribute('data-offset-y', String(offset.y))
        item.setAttribute('data-selected', 'false')

        const entity = space.value.entity.add(id, [item.offsetLeft, item.offsetTop, 0], [item.offsetLeft+item.offsetWidth, item.offsetTop+item.offsetHeight,0], item)
        if (props.debug) item.setAttribute('data-pos', `S_${entity.boundingBox.c1.node}   E_${entity.boundingBox.c2.node}`)

      })
    }

    // Methods
    function getScroll ():Point {
      if (typeof document === 'undefined') return { x: 0, y: 0 }

      return {
        x: el.value.scrollLeft || document.body.scrollLeft || document.documentElement.scrollLeft,
        y: el.value.scrollTop || document.body.scrollTop || document.documentElement.scrollTop
      }
    }

    function drawDebugBounds(x: number, y: number, width: number, height: number, color = 'red') {
      const dbb = document.createElement('div')

      dbb.classList.add('debugbounds')
      dbb.style.position = 'absolute'
      dbb.style.left = `${x+(state.startPoint?.offsetX || 0)}px`
      dbb.style.top = `${y+(state.startPoint?.offsetY || 0)}px`
      dbb.style.width = `${width}px`
      dbb.style.height = `${height}px`
      dbb.style.border = `2px solid ${color}`
      dbb.style.zIndex = '9999'
      dbb.style.opacity = '.75'
      dbb.style.pointerEvents = 'none'

      document.body.appendChild(dbb)
    }

    function onMouseDown(e) {
      if (!e.isTrusted || e.button === 2 || e.srcElement?.draggable) return
      const s = e.srcElement, p: HTMLElement = s?.offsetParent

      if (s && typeof s === 'object' && ('_vei' in s || ('_vei' in p && p.id !== selectorId.value))) return

      state.concat = e.shiftKey
      state.mouseDown = true

      const { x: offsetX, y: offsetY } = el.value ? el.value.getBoundingClientRect() : { x: 0, y: 0 }

      state.startPoint = { x: e.pageX, y: e.pageY, offsetX, offsetY }

      if (props.debug > 1) {
        console.log('start', state.startPoint)
        const drawOctant = (children: typeof space.value.entity.octree.root.children) => {
          children.forEach(e => {
            if (e.children?.length) drawOctant(e.children)
            else drawDebugBounds(e.boundingBox.c1.x, e.boundingBox.c1.y, e.boundingBox.c2.x - e.boundingBox.c1.x, e.boundingBox.c2.y - e.boundingBox.c1.y, `rgba(${RNum.int(0,255)},${RNum.int(0,255)},${RNum.int(0,255)},.5)`)
          })
        }
        // inject quadrant debug bounds
        // inject entity debug bounds
        space.value.entities.forEach(e => drawDebugBounds(e.boundingBox.c1.x, e.boundingBox.c1.y, e.boundingBox.c2.x - e.boundingBox.c1.x, e.boundingBox.c2.y - e.boundingBox.c1.y))
        drawOctant(space.value.entity.octree.root.children)
        drawDebugBounds(space.value.entity.octree.root.boundingBox.c1.x, space.value.entity.octree.root.boundingBox.c1.y, space.value.entity.octree.root.boundingBox.c2.x - space.value.entity.octree.root.boundingBox.c1.x, space.value.entity.octree.root.boundingBox.c2.y - space.value.entity.octree.root.boundingBox.c1.y, 'blue')
      }

      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('mouseup', onMouseUp)

    }

    function onMouseMove (event):void {
      // Update the end point position
      if (!state.mouseDown) return
      state.endPoint = { x: event.pageX, y: event.pageY }
      if (!space.value?.entities?.size) return

      updateSelection()
    }

    const updateSelection = debounce((callback?: (() => void)) => {
      const [ sbx, sby, sbw, sbh ] = selectionBox.value
      if (!state.mouseDown || !space.value?.entities?.size) {
        if (callback) callback()
        return
      }

      // search quadtree for intersection with selectionBox
      const intersectingNodes = space.value.entity.intersect(new BoundingBox(
        space.value.get(sbx, sby, 0),
        space.value.get(sbx+sbw, sby+sbh, 0)
      ))

      if (!state.concat) {
        const diff = difference<SpatialEntity>(state.selection as any, intersectingNodes)
        diff.forEach(i => {
          i.active = false
          state.selection.delete(i)
          i.ref.classList.remove('selected')
        })
      }

      intersectingNodes.forEach(i => {
        i.active = true
        state.selection.add(i)
        i.ref.classList.add('selected')
      })

      if (callback) callback()
  }, props.ticks)

    // FIXME: sometimes clears selection on mouse-up even when selection box has intersections...
    function onMouseUp (event): void {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)

      state.mouseDown = false
      if (props.debug) {
        console.log('MouseUp > selection', state.selection.size)
        document.querySelectorAll('.debugbounds').forEach(element => element.remove())
      }

      // console.log('selection', state.selection.size)
      updateSelection(() => {
        state.concat = false
        state.startPoint = null
        state.endPoint = null
        // console.log('selection', state.selection.size)
        emit('selection', state.selection)
      })
    }

    // function isItemSelected(position: [number, number, number, number, boolean, HTMLElement]): boolean {
    //   if (!position) return false
    //   if (!state.selector || !( (state.path && state.path.length) || position[5].classList.contains(state.selector) )) return false

    //   const [sbx, sby, sbw, sbh] = selectionBox.value
    //   const l = position[0], // left = x
    //   t = position[1], // top = y
    //   w = position[2],
    //   h = position[3]
    //   const i = sbx <= (l + w) && (sbx + sbw) >= l && sby <= (t + h) && (sby + sbh) >= t

    //   if (props.debug) {
    //     position[5].innerHTML = `[Selected: ${(i ? 'YES' : 'NO')}]\n'top '${t} | left ${l} | width ${w} | height ${h}`
    //     position[5].style.borderWidth = '1px'; position[5].style.borderStyle = 'solid'; position[5].style.borderColor = i ? 'blue' : 'red'
    //   }

    //   return !!i
    // }

    onMounted(initSpace)

    onBeforeUnmount(() => {
      // Remove event listeners
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)

      // el.value.$children.forEach((child) => {
      //   child.$off('click')
      // })
    })

    return {
      el,
      ...toRefs(state),
      // computed
      selectionBox,
      selectionBoxStyling,
      // methods
      getScroll,
      onMouseDown,
      onMouseMove,
      onMouseUp,
      selectorId
    }
  }
})

